import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { type Schema, type ConnectorSchemaExtractRequest } from '../../services/api';
import {
  annotateSchema,
  createSchema,
  cloneSchema,
  deleteSchema,
  DRAFT_SCHEMAS_QUERY_KEY,
  updateSchema,
  promoteSchema,
  uploadSchema,
  extractSchemaFromConnector,
  type AnnotationMode,
} from '../../services/schemaApi';
import { prettyPrint } from '../../utils/dataUtils';
import { supportsStructuralInterpretation } from '../../lib/schemaTopology';
import { DEFAULT_NEW_SCHEMA, type SnackbarState } from './types';
import type { AnnotateFormState } from './AnnotateDraftDialog';

const DEFAULT_ANNOTATE_FORM: AnnotateFormState = {
  mode: 'full',
  guidance: '',
  instructions: '',
  includeTemplateRules: true,
};

export function useSchemaMutations(listParams: Record<string, unknown>) {
  const queryClient = useQueryClient();

  const [selectedSchema, setSelectedSchema] = useState<Schema | null>(null);
  const [selectedDraft, setSelectedDraft] = useState<Schema | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState<{ open: boolean; schema?: Schema | null }>({
    open: false,
    schema: null,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<{ open: boolean; schema?: Schema | null }>({
    open: false,
    schema: null,
  });
  const [annotateDialogOpen, setAnnotateDialogOpen] = useState(false);
  const [annotateForm, setAnnotateForm] = useState<AnnotateFormState>(DEFAULT_ANNOTATE_FORM);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState<'file' | 'sql' | 'json' | 'connector' | 'template'>('sql');
  const [uploadContent, setUploadContent] = useState('');
  const [uploadSchemaName, setUploadSchemaName] = useState('');
  const [uploadFiles, setUploadFiles] = useState<{ name: string; content: string; type: 'csv' | 'sql' | 'excel' | 'text' }[]>([]);
  const [uploadFileType, setUploadFileType] = useState<'text' | 'csv' | 'excel' | 'sql'>('text');
  const [templateAdditionalFiles, setTemplateAdditionalFiles] = useState<Record<string, string>>({});
  const [excelSheetNames, setExcelSheetNames] = useState<string[]>([]);
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [selectedConnectorId, setSelectedConnectorId] = useState('');
  const [availableTablesForExtract, setAvailableTablesForExtract] = useState<string[]>([]);
  const [selectedTablesForExtract, setSelectedTablesForExtract] = useState<string[]>([]);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [promoteForm, setPromoteForm] = useState({ name: '', schemaId: '' });
  const [promoteCreateNew, setPromoteCreateNew] = useState(false);
  const [promoteNewSchemaName, setPromoteNewSchemaName] = useState('');
  const [promoteCreating, setPromoteCreating] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editPayloadText, setEditPayloadText] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<'field' | 'json' | 'source'>('field');
  const [editPayload, setEditPayload] = useState<any>(null);
  const [expandedEditTables, setExpandedEditTables] = useState<Set<string>>(new Set());
  const [rulesEditorOpen, setRulesEditorOpen] = useState(false);
  const [rulesEditorContext, setRulesEditorContext] = useState<{ tableName: string; columnName: string } | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>(null);
  const [createForm, setCreateForm] = useState(DEFAULT_NEW_SCHEMA);
  const [cloneForm, setCloneForm] = useState({ name: '', status: 'draft', createdBy: 'web-ui' });


  const invalidateSchemas = () => {
    queryClient.invalidateQueries(['schemas']);
    queryClient.invalidateQueries(['schemas', listParams]);
    // Also invalidate legacy keys for backward compatibility
    queryClient.invalidateQueries(['schema-sets']);
    queryClient.invalidateQueries(['schema-sets', listParams]);
  };

  const createMutation = useMutation(
    () =>
      createSchema({
        tenantId: createForm.tenantId.trim() || undefined,
        schemaName: createForm.name.trim(),
        status: createForm.status.trim() || undefined,
        createdBy: createForm.createdBy?.trim() || undefined,
      }),
    {
      onSuccess: () => {
        invalidateSchemas();
        setSnackbar({ severity: 'success', message: 'Schema created successfully.' });
        setCreateDialogOpen(false);
        setCreateForm(DEFAULT_NEW_SCHEMA);
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Failed to create schema.';
        setSnackbar({ severity: 'error', message });
      },
    }
  );

  const annotateMutation = useMutation(
    (data: { schemaId: string; mode: AnnotationMode; guidance?: string; instructions?: string; includeTemplateRules?: boolean }) =>
      annotateSchema(data.schemaId, {
        mode: data.mode,
        guidance: data.guidance,
        instructions: data.instructions,
        includeTemplateRules: data.includeTemplateRules,
      }),
    {
      onSuccess: (annotatedSchema, variables) => {
        queryClient.invalidateQueries(DRAFT_SCHEMAS_QUERY_KEY);
        if (annotatedSchema) {
          const nextSchema = {
            ...annotatedSchema,
            provenance: {
              ...(annotatedSchema.provenance || {}),
              ...(typeof variables.includeTemplateRules === 'boolean'
                ? { enrichment_include_template_rules: variables.includeTemplateRules }
                : {}),
            },
          };
          setSelectedDraft(nextSchema);
          queryClient.setQueryData(['schema', nextSchema.schema_id], nextSchema);
          queryClient.setQueryData<Schema[] | undefined>(DRAFT_SCHEMAS_QUERY_KEY, (old) => {
            if (!old) return old;
            return old.map((item) =>
              item.schema_id === nextSchema.schema_id ? nextSchema : item
            );
          });
        }
        queryClient.invalidateQueries(['schema']);
        setAnnotateDialogOpen(false);
        setAnnotateForm(DEFAULT_ANNOTATE_FORM);
        const successMessage = (() => {
          if (variables.mode === 'structural') return 'Template interpreted. Tables and output structure populated.';
          if (variables.mode === 'descriptions') return 'Schema descriptions refined.';
          return 'Schema annotated.';
        })();
        setSnackbar({ severity: 'success', message: successMessage });
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Failed to annotate schema.';
        setSnackbar({ severity: 'error', message });
      },
    }
  );

  const updateDraftMutation = useMutation(
    (data: { schemaId: string; definition: any }) =>
      updateSchema(data.schemaId, { definition: data.definition }),
    {
      onSuccess: (updatedSchema) => {
        queryClient.invalidateQueries('schemas-draft');
        setSelectedDraft(updatedSchema);
        setEditDialogOpen(false);
        setEditError(null);
        setSnackbar({ severity: 'success', message: 'Schema updated.' });
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Failed to update schema.';
        setEditError(message);
      },
    }
  );

  const uploadMutation = useMutation(
    (payload: { content: string; sourceType: 'sql_ddl' | 'json_template' | 'file_upload' | 'csv_template' | 'excel_template' | 'template'; schemaName?: string; additionalFiles?: Record<string, string>; selectedSheets?: string[]; selectedColumnsBySheet?: Record<string, string[]> }) =>
      uploadSchema({
        content: payload.content,
        sourceType: payload.sourceType,
        schemaName: payload.schemaName,
        additionalFiles: payload.additionalFiles,
        selectedSheets: payload.selectedSheets,
        selectedColumnsBySheet: payload.selectedColumnsBySheet,
        createdBy: 'web-ui',
      }),
    {
      onSuccess: (data) => {
        if (data.schema) {
          queryClient.setQueryData<Schema[]>('schemas-draft', (old) => {
            const newSchema = data.schema as Schema;
            if (old) {
              return [newSchema, ...old.filter(s => s.schema_id !== newSchema.schema_id)];
            }
            return [newSchema];
          });
        }
        queryClient.invalidateQueries('schemas-draft');
        setUploadDialogOpen(false);
        setUploadContent('');
        setUploadSchemaName('');
        setUploadFiles([]);
        setUploadFileType('text');
        setExcelSheetNames([]);
        setSelectedSheets([]);
        const isTemplate = data.template_summary != null;
        const unresolved = data.template_summary?.xsd_metadata?.resolution?.unresolved_imports;
        const unresolvedSuffix = unresolved?.length
          ? ` (${unresolved.length} referenced schema${unresolved.length > 1 ? 's' : ''} not uploaded — open draft for details)`
          : '';
        setTemplateAdditionalFiles({});
        setSnackbar({
          severity: 'success',
          message: isTemplate
            ? `Template uploaded — ${data.parsed_tables} tables derived.${unresolvedSuffix} Open the draft to run Interpret.`
            : 'Schema uploaded: ' + data.parsed_tables + ' tables, ' + data.parsed_columns + ' columns parsed.',
        });
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Failed to upload schema.';
        setSnackbar({ severity: 'error', message });
      },
    }
  );

  const extractConnectorMutation = useMutation(
    (payload: ConnectorSchemaExtractRequest) =>
      extractSchemaFromConnector(payload),
    {
      onSuccess: (data) => {
        if (data.schema) {
          queryClient.setQueryData<Schema[]>('schemas-draft', (old) => {
            const newSchema = data.schema as Schema;
            if (old) {
              return [newSchema, ...old.filter((s) => s.schema_id !== newSchema.schema_id)];
            }
            return [newSchema];
          });
        }
        queryClient.invalidateQueries('schemas-draft');
        setUploadDialogOpen(false);
        setSelectedConnectorId('');
        setAvailableTablesForExtract([]);
        setSelectedTablesForExtract([]);
        setUploadSchemaName('');
        setSnackbar({
          severity: 'success',
          message: 'Schema extracted: ' + data.parsed_tables + ' tables, ' + data.parsed_columns + ' columns (' + data.extraction_method + ').',
        });
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Failed to extract schema from connector.';
        setSnackbar({ severity: 'error', message });
      },
    }
  );

  const promoteMutation = useMutation(
    (data: { schemaId: string; payload: any }) =>
      promoteSchema(data.schemaId, data.payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schemas-draft');
        invalidateSchemas();
        setPromoteDialogOpen(false);
        setSelectedDraft(null);
        setSnackbar({ severity: 'success', message: 'Schema promoted successfully.' });
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Failed to promote schema.';
        setSnackbar({ severity: 'error', message });
      },
    }
  );

  const cloneMutation = useMutation(
    (schemaId: string) =>
      cloneSchema(schemaId, {
        schemaName: cloneForm.name.trim() || undefined,
        status: cloneForm.status.trim() || undefined,
        createdBy: cloneForm.createdBy.trim() || undefined,
      }),
    {
      onSuccess: () => {
        invalidateSchemas();
        setSnackbar({ severity: 'success', message: 'Schema cloned successfully.' });
        setCloneDialogOpen({ open: false, schema: null });
        setCloneForm({ name: '', status: 'draft', createdBy: 'web-ui' });
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Failed to clone schema.';
        setSnackbar({ severity: 'error', message });
      },
    }
  );

  const deleteMutation = useMutation((schemaId: string) => deleteSchema(schemaId), {
    onSuccess: async (response, schemaId) => {
      if (response?.success === false) {
        return;
      }
      invalidateSchemas();
      await queryClient.refetchQueries(DRAFT_SCHEMAS_QUERY_KEY);
      setSnackbar({ severity: 'success', message: 'Schema deleted.' });
      setDeleteDialogOpen({ open: false, schema: null });
      setSelectedDraft((prev) => {
        if (prev?.schema_id === schemaId) {
          setEditDialogOpen(false);
          setRulesEditorOpen(false);
          return null;
        }
        return prev;
      });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to delete schema.';
      setSnackbar({ severity: 'error', message });
    },
  });

  const handleOpenCreate = () => {
    setCreateForm(DEFAULT_NEW_SCHEMA);
    setCreateDialogOpen(true);
  };

  const handleOpenClone = (schema: Schema) => {
    setCloneForm({
      name: schema.schema_name + ' Copy',
      status: schema.status,
      createdBy: 'web-ui',
    });
    setCloneDialogOpen({ open: true, schema });
  };

  const handleOpenDelete = (schema: Schema) => {
    setDeleteDialogOpen({ open: true, schema });
  };

  const handleAnnotate = (schema: Schema, mode: AnnotationMode = 'full') => {
    setSelectedDraft(schema);
    const needsInterpretation = !!schema.definition?.needs_interpretation;
    const hasTables =
      !!schema.definition?.tables && Object.keys(schema.definition.tables).length > 0;
    const canRunStructural = supportsStructuralInterpretation(schema.definition);
    // Default the dialog's mode to something sensible for the draft's state:
    // brand-new template → structural, otherwise full. Caller can override.
    let initialMode: AnnotationMode = mode
      ? mode
      : needsInterpretation || !hasTables
        ? 'structural'
        : 'full';
    if (!canRunStructural) {
      if (initialMode === 'structural' || initialMode === 'full') {
        initialMode = hasTables ? 'descriptions' : 'full';
      } else if (needsInterpretation || !hasTables) {
        initialMode = hasTables ? 'descriptions' : 'full';
      }
    }
    const includeTemplateRulesFromBackend =
      typeof schema.provenance?.enrichment_include_template_rules === 'boolean'
        ? schema.provenance.enrichment_include_template_rules
        : true;
    setAnnotateForm({
      ...DEFAULT_ANNOTATE_FORM,
      mode: initialMode,
      includeTemplateRules: includeTemplateRulesFromBackend,
    });
    setAnnotateDialogOpen(true);
  };

  const handleEditDraft = (schema: Schema) => {
    setSelectedDraft(schema);

    // Normalize definition: extract tables from definition.tables if present
    // and convert array-format columns to dict format for Field Editor
    const rawDefinition = JSON.parse(JSON.stringify(schema.definition || {}));
    const normalizedDefinition: any = { ...rawDefinition };

    // Extract tables from definition.tables if nested
    const rawTables = rawDefinition.tables || {};

    // Normalize each table's columns from array to dict format
    const normalizedTables: Record<string, any> = {};
    for (const [tableName, tableData] of Object.entries(rawTables)) {
      const tableObj = tableData as any;
      const normalizedTable = { ...tableObj };

      // Convert array columns to dict format
      if (Array.isArray(tableObj.columns)) {
        const columnsDict: Record<string, any> = {};
        for (const col of tableObj.columns) {
          if (col && typeof col === 'object' && col.name) {

            const colName = col.name;
            const { name, ...colRest } = col;
            // Map data_type to type for editor compatibility
            if (colRest.data_type && !colRest.type) {
              colRest.type = colRest.data_type;
            }
            columnsDict[colName] = colRest;
          }
        }
        normalizedTable.columns = columnsDict;
      }
      normalizedTables[tableName] = normalizedTable;
    }

    normalizedDefinition.tables = normalizedTables;

    setEditPayload(normalizedDefinition);
    setEditPayloadText(prettyPrint(schema.definition || {}));
    setEditError(null);
    setEditMode('field');
    setExpandedEditTables(new Set());
    setEditDialogOpen(true);
  };


  const handlePromote = (schema: Schema) => {
    const schemaName = schema.schema_name || schema.definition?.schema_name || '';
    const targetSchemaId = schema.schema_id || '';
    setSelectedDraft(schema);
    setPromoteForm({ name: schemaName, schemaId: targetSchemaId });
    setPromoteCreateNew(false);
    setPromoteNewSchemaName('');
    setPromoteCreating(false);
    setPromoteDialogOpen(true);
  };

  return {
    snackbar, setSnackbar,
    selectedSchema, setSelectedSchema,
    /** @deprecated Use selectedSchema instead */
    selectedSchemaSet: selectedSchema,
    /** @deprecated Use setSelectedSchema instead */
    setSelectedSchemaSet: setSelectedSchema,
    selectedDraft, setSelectedDraft,
    createDialogOpen, setCreateDialogOpen,
    createForm, setCreateForm,
    createMutation,
    handleOpenCreate,
    cloneDialogOpen, setCloneDialogOpen,
    cloneForm, setCloneForm,
    cloneMutation,
    handleOpenClone,
    deleteDialogOpen, setDeleteDialogOpen,
    deleteMutation,
    handleOpenDelete,
    annotateDialogOpen, setAnnotateDialogOpen,
    annotateForm, setAnnotateForm,
    annotateMutation,
    handleAnnotate,
    uploadDialogOpen, setUploadDialogOpen,
    uploadMode, setUploadMode,
    uploadContent, setUploadContent,
    uploadSchemaName, setUploadSchemaName,
    uploadFiles, setUploadFiles,
    uploadFileType, setUploadFileType,
    templateAdditionalFiles, setTemplateAdditionalFiles,
    excelSheetNames, setExcelSheetNames,
    selectedSheets, setSelectedSheets,
    selectedConnectorId, setSelectedConnectorId,
    availableTablesForExtract, setAvailableTablesForExtract,
    selectedTablesForExtract, setSelectedTablesForExtract,
    discoverLoading, setDiscoverLoading,
    uploadMutation,
    extractConnectorMutation,
    promoteDialogOpen, setPromoteDialogOpen,
    promoteForm, setPromoteForm,
    promoteCreateNew, setPromoteCreateNew,
    promoteNewSchemaName, setPromoteNewSchemaName,
    /** @deprecated Use promoteNewSchemaName */
    promoteNewSetName: promoteNewSchemaName,
    /** @deprecated Use setPromoteNewSchemaName */
    setPromoteNewSetName: setPromoteNewSchemaName,
    promoteCreating, setPromoteCreating,
    promoteMutation,
    handlePromote,
    editDialogOpen, setEditDialogOpen,
    editPayloadText, setEditPayloadText,
    editError, setEditError,
    editMode, setEditMode,
    editPayload, setEditPayload,
    expandedEditTables, setExpandedEditTables,
    rulesEditorOpen, setRulesEditorOpen,
    rulesEditorContext, setRulesEditorContext,
    updateDraftMutation,
    handleEditDraft,
  };
}

/** @deprecated Use useSchemaMutations instead */
export const useSchemaSetMutations = useSchemaMutations;
